// Path: src/components/workshoppage/VideoGeneratorTab.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Video, Play, Film, Trash2, Loader2, Sparkles, Copy, Check, AlertCircle, WifiOff, DollarSign, ShieldAlert } from 'lucide-react';
import { 
  generateVideoAPI, 
  getSlideVideoHistoryAPI, 
  deleteVideoHistoryAPI, 
  VideoRecoveryManager,
  handleVideoResponse,
  parseVideoError,
  getVideoErrorMessage,
  VIDEO_ERROR_MESSAGES,
  type VideoHistoryRecord,
  type VideoErrorCode,
  type VideoResult,
  type PollingCallbacks
} from '../../lib/videoManager';

interface VideoGeneratorTabProps {
  slideId: number;
  isActive: boolean;
}

interface VideoErrorState {
  show: boolean;
  errorCode: VideoErrorCode;
  message: string;
}

export const VideoGeneratorTab: React.FC<VideoGeneratorTabProps> = ({ slideId, isActive }) => {
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoRefUrls, setVideoRefUrls] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [history, setHistory] = useState<VideoHistoryRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [errorState, setErrorState] = useState<VideoErrorState>({
    show: false,
    errorCode: 'UNKNOWN_ERROR',
    message: '',
  });
  
  const pollIntervals = useRef<number[]>([]);

  const clearError = () => {
    setErrorState({ show: false, errorCode: 'UNKNOWN_ERROR', message: '' });
  };

  const getErrorIcon = (errorCode: VideoErrorCode) => {
    switch (errorCode) {
      case 'SENSITIVE_CONTENT':
        return <ShieldAlert size={20} className="text-orange-500" />;
      case 'TOKEN_INSUFFICIENT':
        return <DollarSign size={20} className="text-red-500" />;
      case 'NETWORK_ERROR':
        return <WifiOff size={20} className="text-blue-500" />;
      default:
        return <AlertCircle size={20} className="text-red-500" />;
    }
  };

  const getErrorStyle = (errorCode: VideoErrorCode) => {
    switch (errorCode) {
      case 'SENSITIVE_CONTENT':
        return 'bg-orange-50 border-orange-200 shadow-orange-100';
      case 'TOKEN_INSUFFICIENT':
        return 'bg-red-50 border-red-200 shadow-red-100';
      case 'NETWORK_ERROR':
        return 'bg-blue-50 border-blue-200 shadow-blue-100';
      default:
        return 'bg-red-50 border-red-200 shadow-red-100';
    }
  };

  useEffect(() => {
    console.log('[VideoTab] 初始化死而复生机制...');
    
    const pollingCallbacks: PollingCallbacks = {
      onVideoReady: (readyVideo) => {
        console.log('🎉 视频锻造完毕:', readyVideo);
        loadHistory();
        
        if (Notification.permission === 'granted') {
          new Notification('Vibe System: 视频已生成完毕！', { body: readyVideo.prompt });
        } else {
          alert('✨ 您的 AI 视频已在虚空中锻造完毕！');
        }
      },
      onVideoFailed: (error) => {
        console.warn('❌ 视频生成失败:', error);
        loadHistory();
        
        setErrorState({
          show: true,
          errorCode: error.errorCode,
          message: error.errorMessage,
        });
      },
    };
    
    VideoRecoveryManager.resurrectTasks(pollingCallbacks);

    return () => {
      pollIntervals.current.forEach(clearInterval);
      pollIntervals.current = [];
    };
  }, []);

  useEffect(() => {
    if (isActive && slideId) {
      loadHistory();
    }
  }, [isActive, slideId]);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const res = await getSlideVideoHistoryAPI(slideId);
      setHistory(res.data || []);
      
      const generatingRecords = res.data.filter(r => r.status === 'generating');
      
      const pollingCallbacks: PollingCallbacks = {
        onVideoReady: () => {
          loadHistory();
        },
        onVideoFailed: (error) => {
          loadHistory();
          setErrorState({
            show: true,
            errorCode: error.errorCode,
            message: error.errorMessage,
          });
        },
      };
      
      generatingRecords.forEach(record => {
        const intervalId = VideoRecoveryManager.startPolling(slideId, record.user_msg_id, pollingCallbacks);
        if (intervalId) pollIntervals.current.push(intervalId as unknown as number);
      });
      
    } catch (e) {
      console.error('Failed to load video history:', e);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!videoPrompt.trim()) {
      setErrorState({
        show: true,
        errorCode: 'API_ERROR',
        message: '提示词不能为空，请输入您想要生成的视频描述',
      });
      return;
    }

    const refUrls = videoRefUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);

    setIsSubmitting(true);
    clearError();
    
    try {
      const res = await generateVideoAPI({
        slide_id: slideId,
        user_input: videoPrompt,
        reference_image_urls: refUrls.length > 0 ? refUrls : undefined,
        model_override: 'sora'
      });

      const result: VideoResult = handleVideoResponse(res.data);

      if (result.success) {
        if ('pending' in result && result.pending) {
          const msgId = result.userMsgId;
          if (msgId) {
            VideoRecoveryManager.registerTask(slideId, videoPrompt, msgId);
            
            const pollingCallbacks: PollingCallbacks = {
              onVideoReady: () => {
                loadHistory();
              },
              onVideoFailed: (error) => {
                loadHistory();
                setErrorState({
                  show: true,
                  errorCode: error.errorCode,
                  message: error.errorMessage,
                });
              },
            };
            
            const intervalId = VideoRecoveryManager.startPolling(slideId, msgId, pollingCallbacks);
            if (intervalId) pollIntervals.current.push(intervalId as unknown as number);
          }
          
          setVideoPrompt('');
          setVideoRefUrls('');
          await loadHistory();
        } else if ('videoUrl' in result) {
          setVideoPrompt('');
          setVideoRefUrls('');
          await loadHistory();
        }
      } else {
        setErrorState({
          show: true,
          errorCode: result.errorCode,
          message: result.errorMessage,
        });
      }
    } catch (e: any) {
      const errorResult = parseVideoError(e);
      setErrorState({
        show: true,
        errorCode: errorResult.errorCode,
        message: errorResult.errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userMsgId: number) => {
    if (!window.confirm('⚠️ 警告：确定要彻底抹除这条时空记录吗？')) return;
    try {
      await deleteVideoHistoryAPI(userMsgId);
      await loadHistory();
      VideoRecoveryManager.resolveTask(userMsgId);
    } catch (e) {
      alert('删除失败，请重试');
    }
  };

  // 新增：复制 URL 到剪贴板的方法
  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // 新增：一键复制所有成功视频链接
  const handleCopyAllUrls = () => {
    const successRecords = history.filter(r => r.status === 'success' && r.video_url);
    if (successRecords.length === 0) {
      alert('暂无成功的视频链接可复制');
      return;
    }
    const urls = successRecords.map(r => r.video_url);
    const jsonStr = JSON.stringify(urls);
    navigator.clipboard.writeText(jsonStr).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }).catch(err => {
      console.error('复制失败:', err);
      alert('复制失败，请重试');
    });
  };

  if (!isActive) return null;

  return (
    <div className="w-full h-full p-6 overflow-y-auto bg-white/95 backdrop-blur-sm">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-purple-100">
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white p-2.5 rounded-xl shadow-lg shadow-purple-200">
            <Video size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-600">
              AI 视觉重构引擎 (Video Gen)
            </h2>
            <p className="text-sm text-gray-500">通过神经链路将文本与图像转化为动态画面</p>
          </div>
        </div>

        {/* Input Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
          <div className="space-y-2 group">
            <label className="block text-sm font-medium text-gray-700 group-focus-within:text-purple-600 transition-colors">
              参考图像矩阵 (Reference URLs)
            </label>
            <textarea
              value={videoRefUrls}
              onChange={(e) => setVideoRefUrls(e.target.value)}
              placeholder="https://oss.../frame1.png&#10;https://oss.../frame2.png"
              className="w-full h-32 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none font-mono transition-all shadow-inner"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2 group">
            <label className="block text-sm font-medium text-gray-700 group-focus-within:text-purple-600 transition-colors">
              神经提示词 (Prompt) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              placeholder="Neon lights reflecting on wet pavement. Cyberpunk city setup. Slow pan right..."
              className="w-full h-32 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none transition-all shadow-inner"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Generate Button */}
        <div className="flex justify-center pt-2">
          <button
            onClick={handleGenerate}
            disabled={isSubmitting || !videoPrompt.trim()}
            className={`group relative flex items-center gap-2 px-10 py-3.5 rounded-xl font-bold text-white overflow-hidden transition-all duration-300 active:scale-95 ${
              isSubmitting || !videoPrompt.trim()
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg hover:shadow-purple-300/50'
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>正在建立神经连接...</span>
              </>
            ) : (
              <>
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
                <span className="tracking-wide">启动锻造 (GENERATE)</span>
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {errorState.show && (
          <div className={`flex items-start gap-3 p-4 rounded-xl border shadow-sm ${getErrorStyle(errorState.errorCode)}`}>
            {getErrorIcon(errorState.errorCode)}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{errorState.message}</p>
              {errorState.errorCode === 'SENSITIVE_CONTENT' && (
                <p className="text-xs text-gray-500 mt-1">建议：尝试使用更中性的描述词汇，避免敏感话题</p>
              )}
              {errorState.errorCode === 'TOKEN_INSUFFICIENT' && (
                <p className="text-xs text-gray-500 mt-1">提示：请联系您的老师或管理员进行Token充值</p>
              )}
              {errorState.errorCode === 'NETWORK_ERROR' && (
                <p className="text-xs text-gray-500 mt-1">提示：请检查您的网络连接后重试</p>
              )}
            </div>
            <button
              onClick={clearError}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>
        )}

        {/* Vibe Notice */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-4 text-sm text-amber-800 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-amber-400"></div>
          <strong>⚠️ 架构师提示：</strong>
          <ul className="mt-2 space-y-1 ml-4 list-decimal text-amber-700/80">
            <li>视频生成已接入<b>后台幽灵队列</b>，提交后您可以<b>关闭网页、切换标签页</b>，任务不会中断。</li>
            <li>系统会在后台静默轮询，生成完毕后自动在此列表呈现。</li>
          </ul>
        </div>

        {/* Video History Section */}
        <div className="pt-8 mt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Film size={20} className="text-indigo-600" />
              时空记录 (History)
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyAllUrls}
                disabled={isLoadingHistory || history.filter(r => r.status === 'success').length === 0}
                className={`text-sm font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all duration-300 ${
                  copiedAll
                    ? 'bg-green-50 text-green-600 border border-green-200 shadow-[0_0_10px_rgba(74,222,128,0.2)]'
                    : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="一键复制所有成功视频的URL链接"
              >
                {copiedAll ? <Check size={14} /> : <Copy size={14} />}
                {copiedAll ? '已复制 (Copied!)' : '一键复制所有视频链接'}
              </button>
              <button
                onClick={loadHistory}
                disabled={isLoadingHistory}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50 flex items-center gap-1 bg-indigo-50 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                {isLoadingHistory ? <Loader2 size={14} className="animate-spin" /> : null}
                {isLoadingHistory ? '同步中...' : '手动同步'}
              </button>
            </div>
          </div>

          {isLoadingHistory && history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-purple-400/60">
              <Loader2 size={32} className="animate-spin mb-4" />
              <span className="font-mono text-sm tracking-widest">ACCESSING_MATRIX...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-400 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              这里空空如也。提交一个提示词来创造你的第一个奇迹吧。
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((record) => {
                const isGenerating = record.status === 'generating';
                const isSuccess = record.status === 'success';
                const isError = record.status === 'error';
                
                return (
                  <div
                    key={record.user_msg_id}
                    className={`relative rounded-xl p-5 border transition-all duration-500 overflow-hidden ${
                      isError 
                        ? 'bg-red-50/50 border-red-200 shadow-[0_0_15px_rgba(239,68,68,0.1)]' 
                        : isGenerating 
                          ? 'bg-indigo-50/30 border-indigo-200 shadow-[0_0_15px_rgba(99,102,241,0.1)]' 
                          : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    {isGenerating && (
                       <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-indigo-400 to-transparent animate-pulse"></div>
                    )}
                    {isError && (
                       <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-400 to-transparent"></div>
                    )}

                    <div className="flex items-start justify-between gap-6 relative z-10">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-1 rounded-md">
                            {new Date(record.created_at).toLocaleString('zh-CN')}
                          </span>
                          {isSuccess && record.output_money > 0 && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-md font-medium border border-purple-200">
                              耗费: ¥{record.output_money.toFixed(4)}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-800 mb-3 leading-relaxed font-medium">"{record.prompt}"</p>
                        
                        {record.reference_images.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {record.reference_images.map((img, idx) => (
                              <a key={idx} href={img} target="_blank" rel="noopener noreferrer"
                                className="text-[11px] font-medium bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 transition-colors border border-blue-100"
                              >
                                🔗 Ref_Image_{idx + 1}
                              </a>
                            ))}
                          </div>
                        )}

                        <div className="mt-2 flex flex-wrap gap-3">
                          {isError ? (
                            <div className="space-y-2 w-full">
                              <div className="inline-flex items-center gap-2 text-sm font-bold text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
                                <AlertCircle size={16} />
                                <span>生成失败</span>
                              </div>
                              {record.error_message && (
                                <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                                  {record.error_code && <span className="font-mono text-red-400 mr-1">[{record.error_code}]</span>}
                                  {getVideoErrorMessage(record.error_code, record.error_message)}
                                </p>
                              )}
                            </div>
                          ) : isGenerating ? (
                            <div className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100">
                              <Loader2 size={16} className="animate-spin" />
                              <span className="animate-pulse tracking-wide">虚空锻造中... (Forging in the void)</span>
                            </div>
                          ) : (
                            <>
                              <a
                                href={record.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg shadow-sm shadow-green-200 transition-colors"
                              >
                                <Play size={16} className="fill-current" />
                                提取视频 (Play Video)
                              </a>
                              {/* 🌟 复制链接按钮 */}
                              <button
                                onClick={() => handleCopy(record.video_url)}
                                className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border transition-all duration-300 ${
                                  copiedUrl === record.video_url
                                    ? 'bg-green-50 border-green-200 text-green-600 shadow-[0_0_10px_rgba(74,222,128,0.2)]'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-indigo-600 hover:border-indigo-200'
                                }`}
                                title="复制视频原始链接"
                              >
                                {copiedUrl === record.video_url ? <Check size={16} className="animate-in zoom-in" /> : <Copy size={16} />}
                                {copiedUrl === record.video_url ? '已复制 (Copied!)' : '复制链接 (Copy URL)'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleDelete(record.user_msg_id)}
                        className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-100"
                        title="抹除记录"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}} />
    </div>
  );
};